import { useEffect, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { Box, Button, Stack } from "@mui/material";

import { FormSelectField } from "@covertree/ui";
import { UnitLevelUnderwritingAnswers, YesNo } from "@covertree/types";

import { QuestionModalProps } from "../underwritingQuestions";
import { mapQuestionOptionsToFormOptions } from "../../../mappings/underwritingQuestionsMaps";
import { UnderwritingQuestionsForm } from "../../../validatorsAndTypes/underwritingQuestions";

export function SwimmingPoolForm({
  questions,
  closeModal,
}: QuestionModalProps) {
  const { formState, watch, control, setValue } =
    useFormContext<UnderwritingQuestionsForm>();
  const fenceAndSlide = watch(["fourFeetFence", "divingBoard"]);

  const fourFeetFenceOptions = useMemo(
    () =>
      mapQuestionOptionsToFormOptions(
        questions?.[UnitLevelUnderwritingAnswers.FourFeetFence].options
      ),
    [questions]
  );
  const divingBoardOptions = useMemo(
    () =>
      mapQuestionOptionsToFormOptions(
        questions?.[UnitLevelUnderwritingAnswers.DivingBoard].options
      ),
    [questions]
  );

  const divingBoardExtensionOptions = useMemo(() => {
    const divingBoardExtension =
      questions?.[
        UnitLevelUnderwritingAnswers.DivingBoardSlideLiabilityExtension
      ];
    if (!divingBoardExtension) {
      return [];
    }
    return mapQuestionOptionsToFormOptions(divingBoardExtension.options);
  }, [questions]);

  const showLiabilityExtension = useMemo(() => {
    return (
      fenceAndSlide.every((isLiability) => isLiability === "Yes") &&
      divingBoardExtensionOptions.length > 0
    );
  }, [fenceAndSlide, divingBoardExtensionOptions]);

  useEffect(() => {
    if (fenceAndSlide.includes(YesNo.No)) {
      setValue("divingBoardSlideLiabilityExtension", YesNo.No);
    }
  }, [fenceAndSlide]);

  return (
    <Stack spacing={4}>
      <Box>
        <FormSelectField
          control={control}
          name="fourFeetFence"
          id="is-fence-higher-than-4ft'"
          dataTestId="is-fence-higher-than-4ft'"
          options={fourFeetFenceOptions}
          label="Is the pool enclosed by a fence at least 4 feet high?"
        />
      </Box>
      <Box>
        <FormSelectField
          control={control}
          name="divingBoard"
          id="has-slide-or-diving-board"
          dataTestId="has-slide-or-diving-board"
          options={divingBoardOptions}
          label="Does the pool have a slide or diving board?"
        />
      </Box>
      {showLiabilityExtension && (
        <Box>
          <FormSelectField
            control={control}
            name="divingBoardSlideLiabilityExtension"
            id="wants-slide-or-diving-board-coverage"
            dataTestId="wants-slide-or-diving-board-coverage"
            options={divingBoardExtensionOptions}
            label="Would you like liability coverage extended to cover the slide or diving board?"
          />
        </Box>
      )}
      <Box>
        <Button
          type="button"
          onClick={closeModal}
          disabled={!formState.isValid}>
          Save
        </Button>
      </Box>
    </Stack>
  );
}
